import { signal } from '@angular/core';
import { debounce, FieldTree, min, required, SchemaPathTree } from '@angular/forms/signals';
import { FactorsBS7671Dto, FactorsERA6930P3Dto, FactorsERA6930P5Dto, FactorsIEC60502P2Dto, FactorsUDRDto, InstallationDto, SingleCoreArrangementDto } from '../../../core/mceapi.client.generated';
import { apiOpt, apiOptSome, appOpt, getNoneOptionDto } from '../../../core/mceapi.client.helpers';
import { match } from '../../../shared/functions/functional';
import { buildFactorsBS7671Schema, createFactorsBS7671, createFactorsBS7671Dto, defaultFactorsBS7671, FactorsBS7671 } from './factors-bs7671/factors-bs7671.model';
import { buildFactorsERA6930P3Schema, createFactorsERA6930P3, createFactorsERA6930P3Dto, defaultFactorsERA6930P3, FactorsERA6930P3 } from './factors-era6930p3/factors-era6930p3.model';
import { buildFactorsERA6930P5Schema, createFactorsERA6930P5, createFactorsERA6930P5Dto, defaultFactorsERA6930P5, FactorsERA6930P5 } from './factors-era6930p5/factors-era6930p5.model';
import { buildFactorsIEC60502P2Schema, createFactorsIEC60502P2, createFactorsIEC60502P2Dto, defaultFactorsIEC60502P2, FactorsIEC60502P2 } from './factors-iec60502p2/factors-iec60502p2.model';
import { BS7671_STANDARD_GUID, ERA6930P3_STANDARD_GUID, ERA6930P5_STANDARD_GUID, IEC60502P2_STANDARD_GUID, UDR_STANDARD_GUID } from '../../../shared/constants';
import { buildFactorsUdrSchema, createFactorsUdr, createFactorsUdrDto, defaultFactorsUdr, FactorsUdr } from './factors-udr/factors-udr.model';
import { SingleCoreArrangement, buildSingleCoreArrangementSchema, createSingleCoreArrangement, createSingleCoreArrangementDto } from './single-core-arrangement/single-core-arrangement.model';

export interface Installation {
  standardGuid: string,
  circuitLength: number,
  singleCoreArrangement: SingleCoreArrangement,
  udrFactors: FactorsUdr,
  bs7671Factors: FactorsBS7671,
  era6930P3Factors: FactorsERA6930P3,
  era6930P5Factors: FactorsERA6930P5,
  iec60502Factors: FactorsIEC60502P2;
}

export function buildInstallationSchema(path: SchemaPathTree<Installation>) {

  required(path.standardGuid, { message: `Standard is required` });
  
  required(path.circuitLength, { message: `Circuit length is required` });
  min(path.circuitLength, 0, { message: `Circuit length must be positive` });
  debounce(path.circuitLength, 500);

  buildSingleCoreArrangementSchema(path.singleCoreArrangement);
  buildFactorsUdrSchema(path.udrFactors);
  buildFactorsBS7671Schema(path.bs7671Factors);
  buildFactorsERA6930P3Schema(path.era6930P3Factors);
  buildFactorsERA6930P5Schema(path.era6930P5Factors);
  buildFactorsIEC60502P2Schema(path.iec60502Factors);

  required(path.bs7671Factors.primaryMethodGuid, {
    message: 'Primary method is required',
    when: ({ valueOf }) =>
      valueOf(path.standardGuid) === BS7671_STANDARD_GUID
  });

  required(path.bs7671Factors.secondaryMethodGuid, {
    message: 'Secondary method is required',
    when: ({ valueOf }) =>
      valueOf(path.standardGuid) === BS7671_STANDARD_GUID
  });

  required(path.era6930P3Factors.primaryMethodGuid, {
    message: 'Primary method is required',
    when: ({ valueOf }) =>
      valueOf(path.standardGuid) === ERA6930P3_STANDARD_GUID
  });

  required(path.era6930P3Factors.secondaryMethodGuid, {
    message: 'Secondary method is required',
    when: ({ valueOf }) =>
      valueOf(path.standardGuid) === ERA6930P3_STANDARD_GUID
  });

  required(path.era6930P5Factors.primaryMethodGuid, {
    message: 'Primary method is required',
    when: ({ valueOf }) =>
      valueOf(path.standardGuid) === ERA6930P5_STANDARD_GUID
  });

  required(path.era6930P5Factors.secondaryMethodGuid, {
    message: 'Secondary method is required',
    when: ({ valueOf }) =>
      valueOf(path.standardGuid) === ERA6930P5_STANDARD_GUID
  });

  required(path.iec60502Factors.primaryMethodGuid, {
    message: 'Primary method is required',
    when: ({ valueOf }) =>
      valueOf(path.standardGuid) === IEC60502P2_STANDARD_GUID
  });

  required(path.iec60502Factors.secondaryMethodGuid, {
    message: 'Secondary method is required',
    when: ({ valueOf }) =>
      valueOf(path.standardGuid) === IEC60502P2_STANDARD_GUID
  });

}

export function isValidInstallation(installation: FieldTree<Installation>) {
  let factorsValid = false;
  switch (installation.standardGuid().value()) {
    case BS7671_STANDARD_GUID: factorsValid = installation.bs7671Factors().valid(); break;
    case ERA6930P3_STANDARD_GUID: factorsValid = installation.era6930P3Factors().valid(); break;
    case ERA6930P5_STANDARD_GUID: factorsValid = installation.era6930P5Factors().valid(); break;
    case IEC60502P2_STANDARD_GUID: factorsValid = installation.iec60502Factors().valid(); break;
    case UDR_STANDARD_GUID: factorsValid = installation.udrFactors().valid(); break;
    default: factorsValid = false; break;
  }

  return installation().valid() && factorsValid; // && installation.circuitLength().valid() && installation.singleCoreArrangement().valid() && factorsValid;
}

export function createInstallation(dto: InstallationDto) {

  const standardGuid = dto.standardGuid;
  const defaultAirTemperature = dto.projectAirTemperature;
  const defaultGroundTemperature = dto.projectGroundTemperature;
  const defaultSoilThermalResistivity = dto.projectSoilThermalResistivity;

  if (standardGuid === undefined
    || defaultAirTemperature === undefined
    || defaultGroundTemperature === undefined
    || defaultSoilThermalResistivity === undefined) {
    throw new Error('Installation DTO is missing required values.');
  }

  return signal<Installation>({
    standardGuid,
    circuitLength: dto.circuitLength ?? 0,
    singleCoreArrangement: createSingleCoreArrangement(appOpt(dto.singleCoreArrangement ?? {}))(),

    udrFactors:
      match(
        () => defaultFactorsUdr(),
        (x: FactorsUDRDto) => createFactorsUdr(x)())
        (appOpt(dto.udrFactors ?? {})),

    bs7671Factors:
      match(
        () => defaultFactorsBS7671(defaultAirTemperature, defaultGroundTemperature, defaultSoilThermalResistivity),
        (x: FactorsBS7671Dto) => createFactorsBS7671(x, defaultSoilThermalResistivity)())
        (appOpt(dto.bS7671Factors ?? {})),

    era6930P3Factors:
      match(
        () => defaultFactorsERA6930P3(defaultAirTemperature, defaultGroundTemperature, defaultSoilThermalResistivity),
        (x: FactorsERA6930P3Dto) => createFactorsERA6930P3(x, defaultSoilThermalResistivity)())
        (appOpt(dto.erA6930P3Factors ?? {})),

    era6930P5Factors:
      match(
        () => defaultFactorsERA6930P5(defaultAirTemperature, defaultGroundTemperature, defaultSoilThermalResistivity),
        (x: FactorsERA6930P5Dto) => createFactorsERA6930P5(x, defaultSoilThermalResistivity)())
        (appOpt(dto.erA6930P5Factors ?? {})),

    iec60502Factors:
      match(
        () => defaultFactorsIEC60502P2(defaultAirTemperature, defaultGroundTemperature, defaultSoilThermalResistivity),
        (x: FactorsIEC60502P2Dto) => createFactorsIEC60502P2(x, defaultSoilThermalResistivity)())
        (appOpt(dto.ieC60502Factors ?? {})),
  });
}

export function createInstallationDto(installation: FieldTree<Installation>) {

  let base = {
    standardGuid: installation.standardGuid().value(),
    circuitLength: installation.circuitLength().value(),
    singleCoreArrangement: apiOpt(createSingleCoreArrangementDto(installation.singleCoreArrangement)()),
    udrFactors: getNoneOptionDto<FactorsUDRDto>(),
    bS7671Factors: getNoneOptionDto<FactorsBS7671Dto>(),
    erA6930P3Factors: getNoneOptionDto<FactorsERA6930P3Dto>(),
    erA6930P5Factors: getNoneOptionDto<FactorsERA6930P3Dto>(),
    ieC60502Factors: getNoneOptionDto<FactorsIEC60502P2Dto>()
  } as InstallationDto;

  switch (installation.standardGuid().value()) {
    case BS7671_STANDARD_GUID:
      return signal<InstallationDto>(
        { ...base, bS7671Factors: apiOptSome(createFactorsBS7671Dto(installation.bs7671Factors)()) });
    case ERA6930P3_STANDARD_GUID:
      return signal<InstallationDto>(
        { ...base, erA6930P3Factors: apiOptSome(createFactorsERA6930P3Dto(installation.era6930P3Factors)()) });
    case ERA6930P5_STANDARD_GUID:
      return signal<InstallationDto>(
        { ...base, erA6930P5Factors: apiOptSome(createFactorsERA6930P5Dto(installation.era6930P5Factors)()) });
    case IEC60502P2_STANDARD_GUID:
      return signal<InstallationDto>(
        { ...base, ieC60502Factors: apiOptSome(createFactorsIEC60502P2Dto(installation.iec60502Factors)()) });
    case UDR_STANDARD_GUID:
      return signal<InstallationDto>(
        { ...base, udrFactors: apiOptSome(createFactorsUdrDto(installation.udrFactors)()) });
    default:
      return signal<InstallationDto>(base);
  }
}

export function getSecondaryMethodGuid(installation: FieldTree<Installation>) {
  const standardGuid = installation.standardGuid()?.value() ?? '';
    switch(standardGuid) {
      case BS7671_STANDARD_GUID: return signal<string>(installation.bs7671Factors.secondaryMethodGuid().value());
      case ERA6930P3_STANDARD_GUID: return signal<string>(installation.era6930P3Factors.secondaryMethodGuid().value());
      case ERA6930P5_STANDARD_GUID: return signal<string>(installation.era6930P5Factors.secondaryMethodGuid().value());
      case IEC60502P2_STANDARD_GUID: return signal<string>(installation.iec60502Factors.secondaryMethodGuid().value());
      case UDR_STANDARD_GUID: return signal<string>('');
      default: return signal<string>('');
  };
}
